import React from 'react';
import { Check, ArrowRight, AlertTriangle, CheckCircle2, CalendarClock, Hourglass, Ban } from 'lucide-react';

/**
 * "Prossimo passo" per un contenuto del PED: risponde a "cosa devo fare adesso?".
 *
 * Il contenuto ha più stati che si sovrappongono (stato editoriale, Copy/Visual pronto,
 * approvazione, pubblicazione PostProxy, risposta del cliente). Qui li riduciamo a:
 *  - una barra di avanzamento sempre uguale (Copy → Visual → File → Approvato → Programmato → Online)
 *  - UNA sola indicazione con, quando possibile, il pulsante per farla.
 * L'ordine delle regole è l'ordine in cui le cose vanno fatte.
 */

const TAPPE = [
  { k: 'copy', label: 'Copy' },
  { k: 'visual', label: 'Visual' },
  { k: 'file', label: 'File' },
  { k: 'approvato', label: 'Approvato' },
  { k: 'programmato', label: 'Programmato' },
  { k: 'online', label: 'Online' },
];

const fmtData = (d, ora) => {
  if (!d) return '';
  const [y, m, g] = d.slice(0, 10).split('-').map(Number);
  const s = new Date(y, m - 1, g).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
  return ora ? `${s} alle ${String(ora).slice(0, 5)}` : s;
};

/** Calcola tappe completate e la prossima azione. Funzione pura: facile da leggere e da testare. */
export function calcolaPasso({ form, meta, hasMedia, approvato, dataPassata, puoPubblicare, puoApprovare }) {
  const pub = (meta.pub_stato || '').toLowerCase();
  const isStoria = form.formato === 'Storia';
  const manuale = isStoria && form.modalita_storia && form.modalita_storia !== 'Auto';
  const fatto = {
    copy: !!form.copy_ok,
    visual: !!form.visual_ok,
    file: hasMedia,
    approvato,
    programmato: ['scheduled', 'processing', 'published'].includes(pub),
    online: pub === 'published' || form.stato === 'Pubblicato',
  };

  let passo;
  if (form.stato === 'Scartato') {
    passo = { tono: 'muted', Icon: Ban, titolo: 'Contenuto scartato', testo: 'Non verrà pubblicato. Se serve di nuovo, cambia lo stato o duplicalo.' };
  } else if (fatto.online) {
    passo = { tono: 'green', Icon: CheckCircle2, titolo: 'Pubblicato', testo: 'È online. I numeri arrivano da soli nella sezione Pubblicazione.' };
  } else if (meta.cliente_appro === 'modifiche') {
    passo = { tono: 'yellow', Icon: AlertTriangle, titolo: 'Il cliente ha chiesto modifiche', testo: 'Leggi il suo commento in "Stato e approvazioni", aggiorna il contenuto e salva.', vai: 'sez-stato', cta: 'Vedi il commento' };
  } else if (pub === 'failed') {
    passo = { tono: 'red', Icon: AlertTriangle, titolo: 'Pubblicazione non riuscita', testo: 'Leggi l\'errore nella sezione Pubblicazione, correggi (media, data, profilo) e riprogramma.', vai: 'sez-pubblicazione', cta: 'Vai alla pubblicazione' };
  } else if (fatto.programmato) {
    passo = { tono: 'cyan', Icon: CalendarClock, titolo: 'Programmato', testo: `Uscirà da solo${form.data_pubblicazione ? ' ' + fmtData(form.data_pubblicazione, form.ora_pubblicazione) : ''}. Non serve fare altro.` };
  } else if (!form.data_pubblicazione) {
    passo = { tono: 'yellow', titolo: 'Scegli quando esce', testo: 'Imposta la data di pubblicazione (l\'orario è facoltativo).', vai: 'sez-contenuto', cta: 'Imposta la data' };
  } else if (dataPassata) {
    passo = { tono: 'red', Icon: AlertTriangle, titolo: 'La data è passata', testo: 'Questo contenuto non è uscito. Sposta la data in avanti (o scartalo): con una data passata non si può programmare.', vai: 'sez-contenuto', cta: 'Cambia data' };
  } else if (!fatto.copy) {
    passo = { tono: 'yellow', titolo: 'Scrivi il copy', testo: 'Hook e caption. Quando il testo è definitivo, segnalo come pronto.', azione: 'copy_ok', cta: 'Copy pronto', vai2: 'sez-caption', cta2: 'Vai alla caption' };
  } else if (!fatto.visual) {
    passo = { tono: 'purple', Icon: Hourglass, titolo: 'Serve il visual', testo: 'Foto, grafica o video. Se lo fa il videomaker, assegnalo in "Team e produzione"; quando è pronto, segnalo.', azione: 'visual_ok', cta: 'Visual pronto', vai2: 'sez-produzione', cta2: 'Team e produzione' };
  } else if (!fatto.file) {
    passo = { tono: 'purple', titolo: 'Carica il file finale', testo: 'Senza almeno un file il post non può essere programmato.', vai: 'sez-media', cta: 'Carica il file' };
  } else if (!fatto.approvato) {
    passo = puoApprovare
      ? { tono: 'green', titolo: 'Da approvare', testo: 'Controlla anteprima, testo e file: se va bene, approva.', azione: 'approva', cta: 'Approva' }
      : { tono: 'muted', Icon: Hourglass, titolo: 'In attesa di approvazione', testo: 'Il social media manager deve approvarlo. Non serve fare altro per ora.' };
  } else if (manuale) {
    passo = { tono: 'cyan', titolo: 'Da pubblicare a mano', testo: `Storia ${form.modalita_storia}: pubblicala tu ${fmtData(form.data_pubblicazione, form.ora_pubblicazione)}. Riceverai un promemoria.` };
  } else {
    passo = puoPubblicare
      ? { tono: 'cyan', titolo: 'Tutto pronto: programmalo', testo: 'Copy, visual, file e approvazione ci sono. Un clic e uscirà da solo alla data prevista.', azione: 'pubblica', cta: 'Programma su PostProxy' }
      : { tono: 'muted', Icon: Hourglass, titolo: 'Pronto', testo: 'Lo programma il social media manager.' };
  }
  return { fatto, passo };
}

const TONI = {
  yellow: ['var(--yellow-dim)', 'var(--yellow)'],
  purple: ['var(--purple-dim)', 'var(--purple)'],
  green: ['var(--green-dim)', 'var(--green)'],
  cyan: ['var(--cyan-dim)', 'var(--cyan)'],
  red: ['var(--red-dim)', 'var(--red)'],
  muted: ['var(--bg3)', 'var(--text-muted)'],
};

function vaiA(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.animate?.([{ boxShadow: '0 0 0 2px var(--cyan)' }, { boxShadow: '0 0 0 0 transparent' }], { duration: 1400 });
}

export default function ProssimoPasso({ fatto, passo, onAzione, busy }) {
  const [bg, fg] = TONI[passo.tono] || TONI.muted;
  const Icon = passo.Icon || ArrowRight;
  return (
    <section className="eden-card" aria-label="Prossimo passo" style={{ padding: '16px 18px', marginBottom: 14 }}>
      {/* Avanzamento: sempre le stesse tappe, nello stesso ordine (nome della tappa al passaggio del mouse) */}
      <ol aria-label="Avanzamento" style={{ display: 'flex', gap: 4, listStyle: 'none', margin: 0, padding: 0 }}>
        {TAPPE.map(t => (
          <li key={t.k} style={{ flex: 1, height: 4, borderRadius: 4, background: fatto[t.k] ? 'var(--green)' : 'var(--border)' }}
            title={`${t.label}: ${fatto[t.k] ? 'fatto' : 'da fare'}`} aria-label={`${t.label}: ${fatto[t.k] ? 'fatto' : 'da fare'}`} />
        ))}
      </ol>
      <div style={{ fontSize: 11.5, color: 'var(--text-dim)', margin: '7px 0 14px' }}>
        {TAPPE.filter(t => fatto[t.k]).length} di {TAPPE.length} tappe fatte
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ width: 32, height: 32, borderRadius: 9, background: bg, color: fg, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Prossimo passo</div>
          <div style={{ fontSize: 15, fontWeight: 650, color: 'var(--text)', marginTop: 2 }}>{passo.titolo}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.45 }}>{passo.testo}</div>
          {(passo.azione || passo.vai || passo.vai2) && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {passo.azione && (
                <button type="button" className="eden-btn is-primary is-sm" disabled={busy} onClick={() => onAzione(passo.azione)}>
                  <Check size={14} />{passo.cta}
                </button>
              )}
              {passo.vai && (
                <button type="button" className="eden-btn is-sm" onClick={() => vaiA(passo.vai)}>{passo.cta}<ArrowRight size={13} /></button>
              )}
              {passo.vai2 && (
                <button type="button" className="eden-btn is-sm" onClick={() => vaiA(passo.vai2)}>{passo.cta2}<ArrowRight size={13} /></button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
